import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ClipboardCopy, KeyRound, Plus, Send, ShieldAlert, Trash2 } from 'lucide-react';
import type { ApiToken, ApiTokenScope } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { ago } from '@/lib/format';
import { confirmDialog } from './Dialogs';
import { Button, Chip, Empty, IconButton, Input, Modal } from './ui';

const SCOPES: { id: ApiTokenScope; label: string; hint: string }[] = [
  { id: 'send', label: 'Send only', hint: 'Can only send mail from one of your addresses. Nothing else — no reading mail, no settings, no admin. Use this for websites and servers that just send notifications.' },
  { id: 'read', label: 'Read only', hint: 'Can read your mail and settings, but change nothing and send nothing.' },
  { id: 'full', label: 'Full access', hint: 'Everything you can do, including administration if you are an admin. Only for tools you fully trust.' },
];

const scopeLabel = (t: ApiToken) => (t.scope === 'send' ? `send as ${t.sendFrom ?? '?'}` : t.scope === 'read' ? 'read only' : 'full access');

/**
 * Settings → API tokens: long-lived credentials for other systems (e.g. a website's notification emails).
 * The secret is shown exactly once, right after it is created; DTS Mail only keeps its hash.
 */
export function ApiTokensPanel() {
  const qc = useQueryClient();
  const tokens = useQuery({ queryKey: ['api-tokens'], queryFn: api.apiTokens });
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ name: string; value: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['api-tokens'] });
  const list = tokens.data ?? [];

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h2 className="text-[16px] font-semibold">API tokens</h2>
          <p className="mt-0.5 max-w-xl text-[12.5px] text-muted">
            Keys that let another system call DTS Mail as you — for example a website that sends notification emails.
            Give each system its own token with the least access it needs, and revoke it here if it might have leaked.
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>New token</Button>
      </div>

      {list.length === 0 ? (
        <div className="glass rounded-2xl">
          <Empty icon={<KeyRound />} title="No API tokens" hint="Create a send-only token for a website that needs to send email from one of your addresses." />
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((t) => (
            <div key={t.id} className="glass flex items-start gap-4 rounded-2xl px-4 py-3.5">
              <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                {t.scope === 'send' ? <Send className="size-4" /> : t.scope === 'full' ? <ShieldAlert className="size-4" /> : <KeyRound className="size-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[14px] font-semibold">{t.name}</span>
                  <Chip color={t.scope === 'full' ? '#f43f5e' : undefined}>{scopeLabel(t)}</Chip>
                </div>
                <div className="text-[12px] text-muted">
                  <span className="font-mono">{t.prefix}…</span> · created {ago(t.createdUtc)}
                  {' · '}{t.lastUsedUtc ? `last used ${ago(t.lastUsedUtc)}` : 'never used'}
                  {t.expiresUtc && ` · ${new Date(t.expiresUtc) < new Date() ? 'expired' : 'expires'} ${new Date(t.expiresUtc).toLocaleDateString()}`}
                </div>
              </div>
              <IconButton label="Revoke token" onClick={async () => {
                if (!(await confirmDialog(`Revoke ${t.name}?`, 'Anything still using this token stops working immediately. This cannot be undone.', { confirmLabel: 'Revoke', danger: true }))) return;
                await api.revokeApiToken(t.id).then(() => toast.success(`${t.name} revoked`), (e) => toast.error(errorMessage(e)));
                refresh();
              }}><Trash2 /></IconButton>
            </div>
          ))}
        </div>
      )}

      <TokenCreator open={creating} onClose={() => setCreating(false)} onCreated={(name, value) => (setSecret({ name, value }), refresh())} />
      <SecretOnce value={secret} onClose={() => setSecret(null)} />
    </div>
  );
}

function TokenCreator({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (name: string, secret: string) => void }) {
  const me = useMe().data;
  const addresses = (me?.addresses ?? []).filter((a) => a.enabled);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<ApiTokenScope>('send');
  const [from, setFrom] = useState('');
  const [days, setDays] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setScope('send');
    setFrom(addresses[0]?.email ?? '');
    setDays('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const expires = days.trim() ? Number(days) : undefined;
  const validDays = expires === undefined || (Number.isInteger(expires) && expires > 0 && expires <= 3650);
  const valid = name.trim().length > 0 && name.trim().length <= 100 && validDays && (scope !== 'send' || !!from);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.createApiToken(name.trim(), scope, expires, scope === 'send' ? from : undefined);
      onClose();
      onCreated(r.token.name, r.secret);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width={560} title={<span className="flex items-center gap-2"><KeyRound className="size-4 text-accent" />New API token</span>}>
      <div className="space-y-4 p-5">
        <label className="block text-[12px] text-muted">
          Name (what will use it)
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Chuck's Mojito Shack website" maxLength={100} autoFocus />
        </label>
        <div className="text-[12px] text-muted">
          Access
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {SCOPES.map((s) => <Chip key={s.id} active={scope === s.id} onClick={() => setScope(s.id)}>{s.label}</Chip>)}
          </div>
          <p className="mt-1.5 text-[11.5px] text-faint">{SCOPES.find((s) => s.id === scope)?.hint}</p>
        </div>
        {scope === 'send' && (
          <div className="text-[12px] text-muted">
            Sends from
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {addresses.map((a) => <Chip key={a.email} active={from === a.email} onClick={() => setFrom(a.email)}>{a.email}</Chip>)}
              {addresses.length === 0 && <span className="text-faint">You have no addresses to send from.</span>}
            </div>
          </div>
        )}
        <label className="block text-[12px] text-muted">
          Expires after (days, empty = never)
          <Input value={days} onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="365" inputMode="numeric" className="w-32" />
        </label>
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} disabled={!valid} onClick={create}>Create token</Button>
      </div>
    </Modal>
  );
}

/** The secret, once. Closing the dialog forgets it — the server only has its hash. */
function SecretOnce({ value, onClose }: { value: { name: string; value: string } | null; onClose: () => void }) {
  return (
    <Modal open={!!value} onClose={onClose} width={560} title={<span className="flex items-center gap-2"><KeyRound className="size-4 text-accent" />{value?.name}</span>}>
      <div className="space-y-3 p-5">
        <p className="text-[13px]">Copy this token now and store it as a secret setting where it will be used (never in source code or a public file). It won't be shown again.</p>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-bg/40 px-3 py-2.5">
          <code className="min-w-0 flex-1 break-all font-mono text-[12px]">{value?.value}</code>
          <IconButton label="Copy token" onClick={() => value && (navigator.clipboard.writeText(value.value), toast.success('Token copied'))}><ClipboardCopy /></IconButton>
        </div>
      </div>
      <div className="flex justify-end border-t border-line px-5 py-3">
        <Button variant="primary" onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}
